import { useEffect, useState } from 'react';
import { Megaphone, Plus, Loader2, Trash2, Edit2, Users, ExternalLink, CheckCircle, XCircle, Star, Image as ImageIcon, Clock } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';

const emptyForm = { title: '', description: '', deadline: '', spots: '', time_slots: [] };

export const OpenCallsProducerTab = ({ eventId, isEditable }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [viewingApps, setViewingApps] = useState(null);
  const [applications, setApplications] = useState([]);
  const [appsLoading, setAppsLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const load = async () => {
    try {
      const res = await api.get(`/producers/me/events/${eventId}/open-calls`);
      setCalls(res.data?.open_calls || []);
    } catch { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (call) => {
    setEditing(call);
    setForm({
      title: call.title || '',
      description: call.description || '',
      deadline: call.deadline ? String(call.deadline).slice(0, 16) : '',
      spots: call.spots || '',
      time_slots: (call.time_slots || []).map((s) => ({ ...s })),
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.title.trim() || form.title.trim().length < 3) { toast.error('Ingresá un título'); return; }
    if (!form.description.trim() || form.description.trim().length < 10) { toast.error('La descripción debe tener al menos 10 caracteres'); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        deadline: form.deadline ? new Date(form.deadline).toISOString() : null,
        spots: form.spots ? Number(form.spots) : null,
        time_slots: form.time_slots,
      };
      if (editing) {
        await api.put(`/producers/me/events/${eventId}/open-calls/${editing.id}`, payload);
        toast.success('Convocatoria actualizada — las postulaciones se mantienen');
      } else {
        await api.post(`/producers/me/events/${eventId}/open-calls`, payload);
        toast.success('¡Convocatoria publicada! Aparece en el evento y el Portal DJ');
      }
      setShowModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const toggleStatus = async (call) => {
    try {
      await api.put(`/producers/me/events/${eventId}/open-calls/${call.id}`, { status: call.status === 'open' ? 'closed' : 'open' });
      toast.success(call.status === 'open' ? 'Convocatoria cerrada' : 'Convocatoria reabierta');
      load();
    } catch { toast.error('Error al actualizar'); }
  };

  const remove = async (call) => {
    if (!window.confirm(`¿Eliminar la convocatoria "${call.title}"?`)) return;
    try {
      await api.delete(`/producers/me/events/${eventId}/open-calls/${call.id}`);
      toast.success('Convocatoria eliminada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  const openApps = async (call) => {
    setViewingApps(call);
    setAppsLoading(true);
    try {
      const res = await api.get(`/producers/me/events/${eventId}/open-calls/${call.id}/applications`);
      setApplications(res.data?.applications || []);
    } catch { toast.error('Error al cargar postulaciones'); }
    finally { setAppsLoading(false); }
  };

  const setAppStatus = async (app, status) => {
    if (status === 'confirmed') {
      const msg = app.status === 'confirmed'
        ? `¿Generar la placa de ${app.dj_name} y reenviarle el email con la imagen?`
        : `¿Confirmar la participación de ${app.dj_name}? Se genera su placa para Instagram y se le envía por email.`;
      if (!window.confirm(msg)) return;
      setConfirmingId(app.id);
    }
    try {
      const res = await api.put(`/producers/me/events/${eventId}/open-calls/${viewingApps.id}/applications/${app.id}?status=${status}`);
      setApplications((prev) => prev.map((a) => (a.id === app.id
        ? { ...a, status, ...(res.data?.share_images ? { share_images: res.data.share_images } : {}) }
        : a)));
      if (status === 'confirmed') {
        toast.success(res.data?.share_images ? 'Participación confirmada — placa enviada al DJ' : 'Confirmado — email enviado (sin placa)');
      } else {
        toast.success(status === 'accepted' ? 'Postulante seleccionado — email enviado' : 'Postulante rechazado');
      }
    } catch { toast.error('Error al actualizar'); }
    finally { setConfirmingId(null); }
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-white flex items-center gap-2"><Megaphone size={16} className="text-[#D4AF37]" /> Convocatorias DJ</h3>
          <p className="text-xs text-zinc-500 mt-0.5">Abrí slots para que los DJs se postulen desde el Portal DJ</p>
        </div>
        {isEditable && (
          <Button onClick={openCreate} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold" data-testid="producer-create-open-call">
            <Plus size={15} className="mr-1" /> Nueva convocatoria
          </Button>
        )}
      </div>

      {calls.length === 0 ? (
        <div className="text-center py-10 bg-zinc-900/30 rounded-xl border border-dashed border-zinc-800">
          <Megaphone size={28} className="mx-auto text-zinc-700 mb-2" />
          <p className="text-zinc-500 text-sm">Todavía no abriste convocatorias para este evento</p>
        </div>
      ) : (
        <div className="space-y-3">
          {calls.map((call) => (
            <div key={call.id} className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4" data-testid={`producer-open-call-${call.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-white">{call.title}</p>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${call.status === 'open' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-zinc-700/40 text-zinc-400'}`}>
                      {call.status === 'open' ? 'Abierta' : 'Cerrada'}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1 line-clamp-2">{call.description}</p>
                  {(call.time_slots || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {call.time_slots.map((s) => (
                        <span key={s.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#D4AF37]/35 bg-black/40 text-[10px] font-mono font-bold text-[#D4AF37]">
                          <Clock size={9} /> {[s.start_time, s.end_time].filter(Boolean).join('–')}{s.label ? ` · ${s.label}` : ''}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" onClick={() => openApps(call)} className="h-8 border-zinc-700 text-zinc-300 text-xs" data-testid={`producer-view-apps-${call.id}`}>
                    <Users size={13} className="mr-1" /> Postulantes ({call.applications_count ?? 0})
                  </Button>
                  {isEditable && (
                    <>
                      <button onClick={() => openEdit(call)} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg" title="Editar"><Edit2 size={14} /></button>
                      <button onClick={() => toggleStatus(call)} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg" title={call.status === 'open' ? 'Cerrar' : 'Reabrir'}>
                        {call.status === 'open' ? <XCircle size={14} /> : <CheckCircle size={14} />}
                      </button>
                      <button onClick={() => remove(call)} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg" title="Eliminar"><Trash2 size={14} /></button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      <Dialog open={showModal} onOpenChange={(o) => !o && setShowModal(false)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Editar convocatoria' : 'Nueva convocatoria DJ'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400">Título *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: Warm Up — Buscamos DJ" className="bg-zinc-800 border-zinc-700 mt-1" data-testid="producer-oc-title" />
            </div>
            <div>
              <Label className="text-zinc-400">Descripción *</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Qué buscás, género, condiciones..." className="bg-zinc-800 border-zinc-700 mt-1" data-testid="producer-oc-description" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400">Cierre (opcional)</Label>
                <Input type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
              </div>
              <div>
                <Label className="text-zinc-400">Cupos (opcional)</Label>
                <Input type="number" min="0" value={form.spots} onChange={(e) => setForm({ ...form, spots: e.target.value })} className="bg-zinc-800 border-zinc-700 mt-1" />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-zinc-400 text-xs">Horarios buscados (opcional)</Label>
                <button type="button" onClick={() => setForm({ ...form, time_slots: [...form.time_slots, { label: '', start_time: '', end_time: '' }] })} className="text-xs text-[#D4AF37] font-bold hover:underline" data-testid="producer-oc-add-slot">+ Agregar horario</button>
              </div>
              {form.time_slots.length === 0 ? (
                <p className="text-[11px] text-zinc-600 mt-1">Sin horarios: los DJs se postulan a la convocatoria general</p>
              ) : (
                <div className="space-y-2 mt-2">
                  {form.time_slots.map((slot, idx) => (
                    <div key={slot.id || idx} className="grid grid-cols-[1fr,1fr,1.4fr,auto] gap-2 items-center">
                      <Input type="time" value={slot.start_time || ''} onChange={(e) => setForm({ ...form, time_slots: form.time_slots.map((s, i) => i === idx ? { ...s, start_time: e.target.value } : s) })} className="bg-zinc-800 border-zinc-700" />
                      <Input type="time" value={slot.end_time || ''} onChange={(e) => setForm({ ...form, time_slots: form.time_slots.map((s, i) => i === idx ? { ...s, end_time: e.target.value } : s) })} className="bg-zinc-800 border-zinc-700" />
                      <Input placeholder="Warm Up, Closing..." value={slot.label || ''} onChange={(e) => setForm({ ...form, time_slots: form.time_slots.map((s, i) => i === idx ? { ...s, label: e.target.value } : s) })} className="bg-zinc-800 border-zinc-700" />
                      <button type="button" onClick={() => setForm({ ...form, time_slots: form.time_slots.filter((_, i) => i !== idx) })} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 size={14} /></button>
                    </div>
                  ))}
                  <p className="text-[11px] text-zinc-600">Los DJs eligen a qué horario postularse (o a todos). Editar no borra postulaciones.</p>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-zinc-700 text-zinc-300">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#D4AF37] hover:bg-[#B8962E] text-black font-bold" data-testid="producer-oc-save">
              {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null} {editing ? 'Guardar' : 'Publicar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Applications modal */}
      <Dialog open={!!viewingApps} onOpenChange={(o) => !o && setViewingApps(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Postulantes — {viewingApps?.title}</DialogTitle></DialogHeader>
          {appsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#D4AF37]" /></div>
          ) : applications.length === 0 ? (
            <p className="text-zinc-500 text-sm text-center py-8">Todavía no hay postulaciones</p>
          ) : (
            <div className="space-y-3">
              {applications.map((app) => (
                <div key={app.id} className="bg-zinc-800/50 border border-zinc-700/60 rounded-xl p-4" data-testid={`producer-app-${app.id}`}>
                  <div className="flex items-start gap-3">
                    {app.dj_photo_url && <img src={app.dj_photo_url} alt="" className="w-11 h-11 rounded-full object-cover flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-white">{app.dj_name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                          app.status === 'confirmed' ? 'bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40' : app.status === 'accepted' ? 'bg-emerald-500/15 text-emerald-400' : app.status === 'rejected' ? 'bg-red-500/15 text-red-400' : 'bg-yellow-500/15 text-yellow-400'
                        }`}>
                          {app.status === 'confirmed' ? '★ Confirmado' : app.status === 'accepted' ? 'Seleccionado' : app.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                        </span>
                        {app.agency_name && <span className="text-[10px] text-zinc-500">vía {app.agency_name}</span>}
                      </div>
                      <p className="text-xs text-zinc-500 mt-0.5">{app.dj_city} · {(app.dj_genres || []).slice(0, 4).join(', ')}</p>
                      {(viewingApps?.time_slots?.length > 0) && (
                        <p className="text-xs mt-1">
                          <Clock size={10} className="inline mr-1 text-[#D4AF37]" />
                          <span className="text-[#D4AF37] font-bold">{app.slot_labels?.length ? app.slot_labels.join(' · ') : 'Todos los horarios'}</span>
                        </p>
                      )}
                      {app.message && <p className="text-xs text-zinc-400 italic mt-1.5 bg-black/30 rounded-lg px-2.5 py-1.5">"{app.message}"</p>}
                      <div className="flex items-center gap-2 mt-3 flex-wrap">
                        {app.dj_slug && (
                          <a href={`/dj/${app.dj_slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#D4AF37] hover:underline">
                            <ExternalLink size={12} /> Ver Press Kit
                          </a>
                        )}
                        <div className="flex-1" />
                        {(app.status === 'accepted' || (app.status === 'confirmed' && !app.share_images)) && (
                          <Button size="sm" onClick={() => setAppStatus(app, 'confirmed')} disabled={confirmingId === app.id} className="h-7 px-3 bg-[#D4AF37] hover:bg-[#B8962E] text-black text-xs font-bold" data-testid={`producer-confirm-app-${app.id}`}>
                            {confirmingId === app.id ? <Loader2 size={12} className="animate-spin mr-1" /> : <Star size={12} className="mr-1" />}
                            {confirmingId === app.id ? 'Generando placa...' : app.status === 'confirmed' ? 'Generar placa' : 'Confirmar participación'}
                          </Button>
                        )}
                        <Button size="sm" onClick={() => setAppStatus(app, 'accepted')} disabled={app.status === 'accepted' || app.status === 'confirmed'} className="h-7 px-3 bg-emerald-600 hover:bg-emerald-500 text-white text-xs" data-testid={`producer-accept-app-${app.id}`}>
                          <CheckCircle size={12} className="mr-1" /> Aceptar
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setAppStatus(app, 'rejected')} disabled={app.status === 'rejected' || app.status === 'confirmed'} className="h-7 px-3 border-red-500/40 text-red-400 text-xs">
                          <XCircle size={12} className="mr-1" /> Rechazar
                        </Button>
                      </div>
                      {app.status === 'confirmed' && app.share_images && (
                        <div className="flex items-center gap-3 mt-2">
                          <ImageIcon size={13} className="text-[#D4AF37]" />
                          {app.share_images.post && <a href={app.share_images.post} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] hover:underline">Placa Post 1:1</a>}
                          {app.share_images.story && <a href={app.share_images.story} target="_blank" rel="noreferrer" className="text-xs text-[#D4AF37] hover:underline">Placa Story 9:16</a>}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenCallsProducerTab;
